import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PROPERTY_TYPES, LOCATIONS, STRATEGIES, CONTACT_METHODS, FINANCING } from "../lib/constants";
import { Chips, Field } from "./Common";

const STEPS = ["About You", "Property Type", "Location", "Financials", "Strategy", "Requirements", "Submit"];
const EMPTY = {
  full_name: "", company: "", phone: "", email: "", contact_method: "Email", property_types: [], property_type_other: "",
  locations: [], location_details: "", min_price: "", max_price: "", available_capital: "", financing: "",
  desired_cap_rate: "", desired_coc: "", desired_roi: "", min_size: "", max_size: "", units: "", strategies: [],
  strategy_other: "", notes: "", password: "",
};

const Input = ({ f, set, k, ...rest }) => (
  <input className="field" value={f[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`criteria-${k.replace(/_/g, "-")}`} {...rest} />
);

function StepBody({ step, f, set, user, mode }) {
  if (step === 0)
    return (
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Full name *"><Input f={f} set={set} k="full_name" autoComplete="name" /></Field>
        <Field label="Company name"><Input f={f} set={set} k="company" autoComplete="organization" /></Field>
        <Field label="Phone *"><Input f={f} set={set} k="phone" type="tel" inputMode="tel" autoComplete="tel" /></Field>
        <Field label="Email *"><Input f={f} set={set} k="email" type="email" inputMode="email" autoComplete="email" /></Field>
        <div className="sm:col-span-2"><span className="label">Preferred contact method</span><Chips multi={false} options={CONTACT_METHODS} value={f.contact_method} onChange={(v) => set("contact_method", v)} testid="criteria-contact-method" /></div>
      </div>
    );
  if (step === 1)
    return (
      <div className="space-y-5">
        <p className="text-sm text-mist">Select all that apply.</p>
        <Chips options={PROPERTY_TYPES} value={f.property_types} onChange={(v) => set("property_types", v)} testid="criteria-type" />
        {f.property_types.includes("Other") && <Field label="Other property type"><Input f={f} set={set} k="property_type_other" /></Field>}
      </div>
    );
  if (step === 2)
    return (
      <div className="space-y-5">
        <Chips options={LOCATIONS} value={f.locations} onChange={(v) => set("locations", v)} testid="criteria-location" />
        <Field label="Specific cities, counties, ZIP codes or geographic requirements">
          <textarea className="field min-h-[110px]" value={f.location_details} onChange={(e) => set("location_details", e.target.value)} placeholder="e.g. Washington County, 97006, within 20 min of I-5" data-testid="criteria-location-details" />
        </Field>
      </div>
    );
  if (step === 3)
    return (
      <div className="space-y-6">
        <p className="text-sm text-mist">All fields optional — leave return targets blank if you're not sure yet.</p>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Minimum purchase price ($)"><Input f={f} set={set} k="min_price" inputMode="numeric" placeholder="1,000,000" /></Field>
          <Field label="Maximum purchase price ($)"><Input f={f} set={set} k="max_price" inputMode="numeric" placeholder="3,000,000" /></Field>
          <Field label="Available capital ($)"><Input f={f} set={set} k="available_capital" inputMode="numeric" /></Field>
          <Field label="Financing preference">
            <select className="field" value={f.financing} onChange={(e) => set("financing", e.target.value)} data-testid="criteria-financing">
              <option value="">Select…</option>{FINANCING.map((o) => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Desired cap rate (%)"><Input f={f} set={set} k="desired_cap_rate" inputMode="decimal" /></Field>
          <Field label="Desired cash-on-cash (%)"><Input f={f} set={set} k="desired_coc" inputMode="decimal" /></Field>
          <Field label="Desired ROI (%)"><Input f={f} set={set} k="desired_roi" inputMode="decimal" /></Field>
          <Field label="Desired units (if applicable)"><Input f={f} set={set} k="units" inputMode="numeric" /></Field>
          <Field label="Minimum size (SF)"><Input f={f} set={set} k="min_size" inputMode="numeric" /></Field>
          <Field label="Maximum size (SF)"><Input f={f} set={set} k="max_size" inputMode="numeric" /></Field>
        </div>
      </div>
    );
  if (step === 4)
    return (
      <div className="space-y-5">
        <Chips options={STRATEGIES} value={f.strategies} onChange={(v) => set("strategies", v)} testid="criteria-strategy" />
        {f.strategies.includes("Other") && <Field label="Other strategy"><Input f={f} set={set} k="strategy_other" /></Field>}
      </div>
    );
  if (step === 5)
    return (
      <Field label="Tell us anything else we should know about your ideal investment.">
        <textarea className="field min-h-[220px]" value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Condition, tenancy, zoning, timeline, deal-breakers…" data-testid="criteria-notes" />
      </Field>
    );
  return (
    <div className="space-y-6">
      <dl className="grid gap-4 sm:grid-cols-2 text-sm">
        {[["Contact", `${f.full_name} · ${f.email}`], ["Property types", f.property_types.join(", ") || "Any"], ["Markets", f.locations.join(", ") || "Any"],
          ["Price range", f.min_price || f.max_price ? `$${f.min_price || "0"} – $${f.max_price || "open"}` : "Flexible"], ["Strategy", f.strategies.join(", ") || "Open"]].map(([k, v]) => (
          <div key={k} className="border-l-2 border-gold pl-4"><dt className="text-[11px] uppercase tracking-wider text-mist">{k}</dt><dd className="mt-1 text-navy">{v}</dd></div>
        ))}
      </dl>
      {mode === "public" && !user && (
        <Field label="Create a portal password (optional)" hint="Set a password to track matched opportunities in your investor portal. Min 8 characters.">
          <input type="password" className="field" value={f.password} onChange={(e) => set("password", e.target.value)} autoComplete="new-password" data-testid="criteria-password" />
        </Field>
      )}
      <p className="text-xs text-mist leading-relaxed">By submitting, you agree to be contacted by Sido Holdings regarding your criteria. Submitting criteria does not create an obligation, and Sido Holdings does not guarantee that a matching opportunity will be found. See our <Link to="/legal/privacy" className="underline">Privacy Policy</Link>.</p>
    </div>
  );
}

export default function CriteriaForm({ initial, mode = "public", onDone }) {
  const { user, signIn } = useAuth();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ ...EMPTY, ...(user ? { full_name: user.name || "", email: user.email, phone: user.phone || "", company: user.company || "" } : {}), ...(initial || {}) });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const next = () => {
    if (step === 0 && (!f.full_name.trim() || !f.email.trim() || !f.phone.trim())) return toast.error("Please add your name, phone and email.");
    if (step === 0 && !/^\S+@\S+\.\S+$/.test(f.email)) return toast.error("Please enter a valid email.");
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const submit = async () => {
    setBusy(true);
    try {
      const body = { ...f, password: f.password || null };
      if (mode === "edit") {
        await api.put("/me/criteria", body);
        toast.success("Criteria updated");
      } else {
        const { data } = await api.post("/criteria", body);
        if (data.token) signIn(data);
      }
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-navy/10" data-testid="criteria-form">
      <div className="border-b border-navy/10 px-5 sm:px-10 pt-6 pb-5">
        <div className="flex items-center justify-between text-xs">
          <span className="font-mono text-gold" data-testid="criteria-step-indicator">STEP {String(step + 1).padStart(2, "0")} / 07</span>
          <span className="text-mist">{STEPS[step]}</span>
        </div>
        <div className="mt-4 grid grid-cols-7 gap-1">
          {STEPS.map((s, i) => <span key={s} className={`h-[3px] transition-colors duration-500 ${i <= step ? "bg-navy" : "bg-navy/10"}`} />)}
        </div>
      </div>
      <div className="px-5 sm:px-10 py-8 sm:py-10">
        <h3 className="text-3xl sm:text-4xl font-medium text-navy mb-8">{["About You", "What Are You Looking For?", "Location", "Financial Criteria", "Investment Strategy", "Property Requirements", "Review & Submit"][step]}</h3>
        <StepBody step={step} f={f} set={set} user={user} mode={mode} />
      </div>
      <div className="flex gap-3 border-t border-navy/10 px-5 sm:px-10 py-5">
        {step > 0 && <button type="button" onClick={() => setStep(step - 1)} className="btn-ghost text-navy hover:bg-navy/5" data-testid="criteria-back"><ArrowLeft className="h-4 w-4" /> Back</button>}
        {step < STEPS.length - 1 ? (
          <button type="button" onClick={next} className="btn-primary ml-auto flex-1 sm:flex-none" data-testid="criteria-next">Continue <ArrowRight className="h-4 w-4" /></button>
        ) : (
          <button type="button" onClick={submit} disabled={busy} className="btn-gold ml-auto flex-1 sm:flex-none" data-testid="criteria-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {mode === "edit" ? "Save Criteria" : "Start My Property Search"}
          </button>
        )}
      </div>
    </div>
  );
}
